import React, { useState } from 'react';
import { useTournament } from '../hooks/useTournament';

const ExcelImporter = () => {
  const { addPlayer, players } = useTournament();
  const [importMessage, setImportMessage] = useState(null);

  // Function to handle file import for a simple list of names
  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Accept only CSV files
    if (!file.name.endsWith('.csv')) {
      setImportMessage({ type: 'error', text: 'Bitte wählen Sie eine CSV-Datei aus.' });
      return;
    }

    // Validate file size (max 1MB for CSV)
    if (file.size > 1024 * 1024) {
      setImportMessage({ type: 'error', text: 'Datei ist zu groß (max. 1MB).' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let content = e.target.result;

        // Validate content is a string
        if (typeof content !== 'string') {
          throw new Error('Ungültiger Dateiinhalt');
        }

        // Remove UTF-8 BOM if present
        if (content.charCodeAt(0) === 0xFEFF) {
          content = content.slice(1);
        }

        const lines = content.split(/\r?\n/);

        // Validate header (first line)
        if (lines.length < 2) {
          setImportMessage({ type: 'error', text: 'CSV-Datei ist leer oder hat kein gültiges Format.' });
          return;
        }

        let importCount = 0;
        const errors = [];

        // Skip header and parse each line
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          let playerName;
          if (line.includes(';')) {
            const parts = line.split(';');
            // Validate that we have at least 2 parts (number and name)
            if (parts.length < 2) {
              errors.push(`Zeile ${i + 1}: Ungültiges Format`);
              continue;
            }
            playerName = parts[1]?.trim();
          } else {
            playerName = line;
          }

          // Validate player name
          if (!playerName) {
            continue; // Skip empty names
          }

          // Validate player name length (max 50 characters)
          if (playerName.length > 50) {
            errors.push(`Zeile ${i + 1}: Name zu lang (max. 50 Zeichen)`);
            continue;
          }

          // Check if we've reached the player limit
          if (players.length + importCount >= 100) {
            errors.push('Maximale Spieleranzahl (100) erreicht');
            break;
          }

          addPlayer(playerName);
          importCount++;
        }

        // Show results
        if (importCount > 0) {
          let message = `${importCount} Spieler erfolgreich importiert.`;
          if (errors.length > 0) {
            message += ` ${errors.length} Fehler.`;
          }
          setImportMessage({ type: 'success', text: message });
        } else if (errors.length > 0) {
          setImportMessage({ type: 'error', text: `Import fehlgeschlagen: ${errors[0]}` });
        } else {
          setImportMessage({ type: 'error', text: 'Keine Spieler zum Importieren gefunden.' });
        }

        event.target.value = '';
      } catch (error) {
        setImportMessage({ type: 'error', text: 'Fehler beim Importieren der Datei. Bitte überprüfen Sie das Format.' });
      }
    };

    reader.onerror = () => {
      setImportMessage({ 
        type: 'error', 
        text: 'Fehler beim Lesen der Datei.' 
      });
    };
    
    // Read file with UTF-8 encoding to properly handle German characters
    reader.readAsText(file, 'UTF-8');
  };

  // Function to create CSV template with Nummer;Spieler structure
  const generateAndDownloadTemplate = () => {
    let csvContent = 'Nummer;Spieler\n';
    const sampleNames = ['Müller', 'Schäfer', 'Köhler', 'König', 'Weiß', 'Löwe', 'Bäcker'];
    for (let i = 1; i <= 100; i++) {
      const name = i <= 7 ? sampleNames[i - 1] : '';
      csvContent += `${i};${name}\n`;
    }
    // Add UTF-8 BOM to ensure proper encoding for German characters
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'player_import.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };  // Function to export current players to CSV
  const exportPlayersToCSV = () => {
    const playerList = players;
    let csvContent = 'Nummer;Spieler\n';
    
    playerList.forEach((playerName, index) => {
      csvContent += `${index + 1};${playerName}\n`;
    });
    
    // Add UTF-8 BOM to ensure proper encoding for German characters
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = 'spieler_export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="importer-section">
      <div className="import-export-row">
        <button 
          onClick={generateAndDownloadTemplate}
          className="btn-compact btn-template"
        >
          Vorlage (.csv)
        </button>
        
        <div className="file-input-container">
          <input
            id="file-upload"
            type="file"
            accept=".csv"
            onChange={handleFileImport}
            className="file-input"
          />
          <label htmlFor="file-upload" className="file-input-label">
            📥 Import
          </label>
        </div>
        
        <button 
          onClick={exportPlayersToCSV}
          className="btn-compact btn-export"
        >
          📤 Export
        </button>
      </div>
      
      {importMessage && (
        <div className={`import-message ${importMessage.type}`}>
          {importMessage.text}
        </div>
      )}
    </div>
  );
};

export default ExcelImporter;